import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Star, Send, Loader2, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { SmallText, BodyText } from './brand/BrandTypography';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BetaFeedbackWidget - Non-intrusive feedback collector for beta testers
 * 
 * Behavior:
 * - Only shows for beta testers
 * - Appears as a small floating button
 * - Never interrupts the user — they initiate feedback
 * - Occasionally shows a gentle "nudge" badge (max once per session)
 * - Rewards feedback with extra trial days
 */
const BetaFeedbackWidget = () => {
    const { isAuthenticated, token } = useAuth();
    const [betaStatus, setBetaStatus] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showNudge, setShowNudge] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [feedbackType, setFeedbackType] = useState('quick');
    const [formData, setFormData] = useState({
        rating: 0,
        feedback_text: '',
        tool_id: '',
        would_recommend: null
    });

    useEffect(() => {
        if (isAuthenticated && token) {
            fetchBetaStatus();
        }
    }, [isAuthenticated, token]);

    useEffect(() => {
        // Show gentle nudge after 5 minutes if user hasn't given feedback
        // Only once per session
        if (betaStatus?.is_beta_tester && betaStatus?.can_earn_bonus) {
            const nudgeShown = sessionStorage.getItem('beta_nudge_shown');
            if (!nudgeShown) {
                const timer = setTimeout(() => {
                    setShowNudge(true);
                    sessionStorage.setItem('beta_nudge_shown', 'true');
                    // Hide nudge after 10 seconds
                    setTimeout(() => setShowNudge(false), 10000);
                }, 5 * 60 * 1000); // 5 minutes
                
                return () => clearTimeout(timer);
            }
        }
    }, [betaStatus]);

    const fetchBetaStatus = async () => {
        try {
            const response = await fetch(`${API_URL}/api/beta-program/my-status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setBetaStatus(data);
            }
        } catch (error) {
            console.error('Failed to fetch beta status:', error);
        }
    };

    const handleSubmit = async () => {
        if (!formData.feedback_text.trim()) {
            toast.error('Please share some feedback');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/beta-program/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    feedback_type: feedbackType,
                    rating: formData.rating || null,
                    feedback_text: formData.feedback_text,
                    tool_id: formData.tool_id || null,
                    would_recommend: formData.would_recommend
                })
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(data.message || 'Thank you for your feedback!');
                setIsOpen(false);
                setFormData({ rating: 0, feedback_text: '', tool_id: '', would_recommend: null });
                fetchBetaStatus(); // Refresh status
            } else {
                const error = await response.json();
                toast.error('Failed to submit', { description: error.detail });
            }
        } catch (error) {
            console.error('Feedback submit error:', error);
            toast.error('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    // Don't render if not a beta tester
    if (!betaStatus?.is_beta_tester) return null;

    return (
        <>
            {/* Floating Button */}
            <AnimatePresence>
                {!isOpen && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="fixed bottom-24 right-4 z-40"
                    >
                        <button
                            onClick={() => setIsOpen(true)}
                            className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] 
                                     text-white shadow-lg hover:shadow-xl transition-all hover:scale-105
                                     flex items-center justify-center"
                            data-testid="beta-feedback-btn"
                        >
                            <MessageSquare className="w-6 h-6" />
                            
                            {/* Nudge badge */}
                            {showNudge && betaStatus.can_earn_bonus && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#FFBB98] 
                                             flex items-center justify-center"
                                >
                                    <Gift className="w-3 h-3 text-white" />
                                </motion.div>
                            )}
                        </button>
                        
                        {/* Nudge tooltip */}
                        {showNudge && betaStatus.can_earn_bonus && (
                            <motion.div
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="absolute bottom-full right-0 mb-2 mr-2 px-3 py-2 rounded-lg 
                                         bg-white shadow-lg whitespace-nowrap"
                            >
                                <SmallText className="text-[#1D6B6B] font-medium">
                                    Share feedback, earn +7 days!
                                </SmallText>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Feedback Panel */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-50"
                    >
                        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#E8E4DC]">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-[#1D6B6B] to-[#145555] px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-white">
                                    <MessageSquare className="w-5 h-5" />
                                    <span className="font-medium">Beta Feedback</span>
                                    {betaStatus.can_earn_bonus && (
                                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                            +7 days
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button 
                                        onClick={() => setIsMinimized(!isMinimized)}
                                        className="p-1 hover:bg-white/20 rounded"
                                    >
                                        {isMinimized ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
                                    </button>
                                    <button 
                                        onClick={() => setIsOpen(false)}
                                        className="p-1 hover:bg-white/20 rounded"
                                    >
                                        <X className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            {!isMinimized && (
                                <div className="p-4 max-h-[60vh] overflow-y-auto">
                                    {/* Feedback Type Tabs */}
                                    <div className="flex gap-1 mb-4 bg-[#F5F0EB] rounded-lg p-1">
                                        {[
                                            { id: 'quick', label: 'Quick' },
                                            { id: 'detailed', label: 'Detailed' },
                                            { id: 'bug', label: 'Bug' },
                                            { id: 'feature', label: 'Idea' }
                                        ].map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setFeedbackType(tab.id)}
                                                className={`flex-1 px-2 py-1.5 rounded-md text-sm font-medium transition-all ${
                                                    feedbackType === tab.id
                                                        ? 'bg-white text-[#1D6B6B] shadow-sm'
                                                        : 'text-[#7D8E95] hover:text-[#5A6A6A]'
                                                }`}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Quick Rating */}
                                    {feedbackType === 'quick' && (
                                        <div className="mb-4">
                                            <SmallText className="text-[#7D8E95] mb-2 block">
                                                How's your experience so far?
                                            </SmallText>
                                            <div className="flex gap-2 justify-center">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <button
                                                        key={star}
                                                        onClick={() => setFormData({...formData, rating: star})}
                                                        className="p-1 transition-transform hover:scale-110"
                                                    >
                                                        <Star 
                                                            className={`w-8 h-8 ${
                                                                star <= formData.rating 
                                                                    ? 'text-[#FFBB98] fill-[#FFBB98]' 
                                                                    : 'text-[#E8E4DC]'
                                                            }`} 
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Tool Selection (for detailed/bug) */}
                                    {(feedbackType === 'detailed' || feedbackType === 'bug') && (
                                        <div className="mb-4">
                                            <SmallText className="text-[#7D8E95] mb-2 block">
                                                Which tool is this about? (optional)
                                            </SmallText>
                                            <select
                                                value={formData.tool_id}
                                                onChange={(e) => setFormData({...formData, tool_id: e.target.value})}
                                                className="w-full px-3 py-2 rounded-lg bg-[#F5F0EB] border-0 text-sm
                                                         focus:ring-2 focus:ring-[#1D6B6B] outline-none"
                                            >
                                                <option value="">General / Not specific</option>
                                                <option value="sage">AI Guide (Sage)</option>
                                                <option value="calm-cycle">Calm Cycle</option>
                                                <option value="still-sphere">Still Sphere</option>
                                                <option value="meditations">Guided Meditations</option>
                                                <option value="breathing">Breathing Exercises</option>
                                                <option value="infinity-loop">Infinity Loop</option>
                                                <option value="relationship-tools">Relationship Tools</option>
                                                <option value="partner-mode">Partner Mode</option>
                                                <option value="check-in">Daily Check-in</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    )}

                                    {/* Feedback Text */}
                                    <div className="mb-4">
                                        <SmallText className="text-[#7D8E95] mb-2 block">
                                            {feedbackType === 'quick' && 'Anything else you want to share?'}
                                            {feedbackType === 'detailed' && 'Tell us more about your experience'}
                                            {feedbackType === 'bug' && 'Describe what went wrong'}
                                            {feedbackType === 'feature' && 'What feature would you love to see?'}
                                        </SmallText>
                                        <textarea
                                            value={formData.feedback_text}
                                            onChange={(e) => setFormData({...formData, feedback_text: e.target.value})}
                                            placeholder={
                                                feedbackType === 'quick' ? 'Optional thoughts...' :
                                                feedbackType === 'bug' ? 'What happened? What did you expect?' :
                                                feedbackType === 'feature' ? 'Describe your idea...' :
                                                'Share your thoughts...'
                                            }
                                            rows={3}
                                            className="w-full px-3 py-2 rounded-lg bg-[#F5F0EB] border-0 text-sm
                                                     resize-none focus:ring-2 focus:ring-[#1D6B6B] outline-none"
                                        />
                                    </div>

                                    {/* Would Recommend (for detailed) */}
                                    {feedbackType === 'detailed' && (
                                        <div className="mb-4">
                                            <SmallText className="text-[#7D8E95] mb-2 block">
                                                Would you recommend CoRegulate to a friend with ADHD?
                                            </SmallText>
                                            <div className="flex gap-2">
                                                {[
                                                    { value: true, label: 'Yes!' },
                                                    { value: false, label: 'Not yet' }
                                                ].map(opt => (
                                                    <button
                                                        key={String(opt.value)}
                                                        onClick={() => setFormData({...formData, would_recommend: opt.value})}
                                                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                            formData.would_recommend === opt.value
                                                                ? 'bg-[#1D6B6B] text-white'
                                                                : 'bg-[#F5F0EB] text-[#5A6A6A] hover:bg-[#E8E4DC]'
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting || (!formData.feedback_text.trim() && !formData.rating)}
                                        className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1D6B6B] to-[#145555] 
                                                 text-white font-medium flex items-center justify-center gap-2
                                                 hover:opacity-90 transition-opacity disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                        Send Feedback
                                    </button>

                                    {/* Status */}
                                    <div className="mt-3 text-center">
                                        <SmallText className="text-[#7D8E95]">
                                            {betaStatus.days_remaining} days left • {betaStatus.feedback_submitted} feedback{betaStatus.feedback_submitted !== 1 ? 's' : ''} submitted
                                        </SmallText>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default BetaFeedbackWidget;
